import React, { useState, useEffect } from "react";
import { Modal, Select, Input, DatePicker, TimePicker, Switch } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import { fetchAvailableInterviewers, fetchJobRounds, scheduleInterviewRound } from "../../../../core/data/redux/recruitment/thunks";
import { selectAvailableInterviewers, selectAvailableInterviewersLoading, selectJobRounds, selectJobRoundsLoading } from "../../../../core/data/redux/recruitment/selectors";
import type { ApplicationRound } from "../../../../core/data/redux/recruitment/types";

interface Props {
  round?: ApplicationRound | null;
  applicationId: number | null;
  jobId?: number | null;
  candidatePhone?: string;
  defaultRoundId?: number;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type InterviewMode = "Video Call" | "Phone" | "In-Person";

const INTERVIEW_MODE_OPTIONS: { value: InterviewMode; label: string; icon: string }[] = [
  { value: "Video Call", label: "Video Call", icon: "ti-video" },
  { value: "Phone", label: "Phone Call", icon: "ti-phone" },
  { value: "In-Person", label: "In-Person", icon: "ti-building" },
];

const MODE_FIELD: Record<InterviewMode, { label: string; placeholder: string; icon: string; required: boolean }> = {
  "Video Call": { label: "Meeting link", placeholder: "https://meet.google.com/…", icon: "ti-link", required: true },
  "Phone": { label: "Candidate phone number", placeholder: "+91 98765 43210", icon: "ti-phone", required: true },
  "In-Person": { label: "Venue / address", placeholder: "3rd Floor, Tower B, Cyber Park", icon: "ti-map-pin", required: false },
};

const Label: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
  <label style={{
    display: "block", fontSize: 11, fontWeight: 700, marginBottom: 5,
    color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px"
  }}>
    {text}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </label>
);

const ScheduleInterviewModal: React.FC<Props> = ({ round, applicationId, jobId, candidatePhone, defaultRoundId, open, onClose, onSuccess }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);

  // Form State
  const [interviewDate, setInterviewDate] = useState("");
  const [interviewTime, setInterviewTime] = useState("");
  const [duration, setDuration] = useState<number>(60);
  const [interviewMode, setInterviewMode] = useState<InterviewMode>("Video Call");
  const [interviewLink, setInterviewLink] = useState("");
  const [interviewPhone, setInterviewPhone] = useState("");
  const [interviewLocation, setInterviewLocation] = useState("");
  const [interviewerIds, setInterviewerIds] = useState<number[]>([]);
  const [sendNotification, setSendNotification] = useState(true);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const employeeOptions = useSelector(selectAvailableInterviewers).map((u: any) => ({
    value: u.user_id,
    label: u.name || `${u.fname || ""} ${u.lname || ""}`.trim(),
  }));
  const employeesLoading = useSelector(selectAvailableInterviewersLoading);

  const [selectedRoundType, setSelectedRoundType] = useState<string>("");
  const [selectedRoundNumber, setSelectedRoundNumber] = useState<number>(1);
  const [selectedRoundId, setSelectedRoundId] = useState<number | undefined>(undefined);

  const jobRounds = useSelector(selectJobRounds);
  const jobRoundsLoading = useSelector(selectJobRoundsLoading);

  useEffect(() => {
    if (open) {
      dispatch(fetchAvailableInterviewers({}));
      if (!round && jobId) {
        dispatch(fetchJobRounds(jobId));
      }
      setInterviewDate("");
      setInterviewTime("");
      setDuration(round?.duration_minutes || 60);
      setInterviewMode("Video Call");
      setInterviewLink("");
      setInterviewPhone(candidatePhone || "");
      setInterviewLocation("");
      setInterviewerIds([]);
      setSendNotification(true);
      setFieldErrors({});
      if (!round) {
        if (defaultRoundId) {
          setSelectedRoundId(defaultRoundId);
        } else {
          setSelectedRoundId(undefined);
          setSelectedRoundType("");
          setSelectedRoundNumber(1);
        }
      }
    }
  }, [open, round, jobId, dispatch, defaultRoundId, candidatePhone]);

  useEffect(() => {
    if (selectedRoundId && jobRounds && jobRounds.length > 0) {
      const selected = jobRounds.find((r: any) => r.id === selectedRoundId);
      if (selected && !selectedRoundType) {
        setSelectedRoundType(selected.round_type);
        setSelectedRoundNumber(selected.stage_order || 1);
        setDuration(selected.duration_minutes || 60);
      }
    }
  }, [selectedRoundId, jobRounds, selectedRoundType]);

  const modeField = MODE_FIELD[interviewMode];
  const modeFieldValue = interviewMode === "Video Call" ? interviewLink : interviewMode === "Phone" ? interviewPhone : interviewLocation;

  const handleModeChange = (val: InterviewMode) => {
    setInterviewMode(val);
    setInterviewLink("");
    setInterviewPhone(candidatePhone || "");
    setInterviewLocation("");
    setFieldErrors({});
  };

  const handleModeFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (interviewMode === "Video Call") setInterviewLink(e.target.value);
    else if (interviewMode === "Phone") setInterviewPhone(e.target.value);
    else setInterviewLocation(e.target.value);
  };

  const handleSubmit = async () => {
    if (!applicationId) return;

    const errs: Record<string, string> = {};
    if (!round && !selectedRoundType) errs.round = "Select an interview round";
    if (!interviewDate) errs.interviewDate = "Date is required";
    if (!interviewTime) errs.interviewTime = "Time is required";
    if (modeField.required && !modeFieldValue.trim()) errs.modeField = `${modeField.label} is required`;

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    setLoading(true);
    try {
      const scheduledAt = `${interviewDate}T${interviewTime}:00Z`;

      await dispatch(scheduleInterviewRound({
        application_id: applicationId,
        round_type: round ? round.round_type : selectedRoundType,
        round_number: round ? round.stage_order : selectedRoundNumber,
        scheduled_at: scheduledAt,
        interviewer_ids: interviewerIds.length > 0 ? interviewerIds : undefined,
        duration_minutes: duration,
        interview_mode: interviewMode === "Video Call" ? "video_call" : interviewMode === "Phone" ? "phone" : "in_person",
        interview_link: interviewMode === "Video Call" ? interviewLink.trim() : undefined,
        interview_location: interviewMode === "Video Call" ? undefined : interviewMode === "In-Person" ? interviewLocation.trim() : interviewPhone.trim(),
        send_notification: sendNotification
      })).unwrap();

      onSuccess();
      onClose();
    } catch (e: any) {
      console.error(e);
      // In a real app we'd trigger a toast. Here we just unlock out.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
            border: "1px solid #ddd6fe", display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <i className="ti ti-calendar-plus" style={{ fontSize: 17, color: "#7c3aed" }} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", lineHeight: 1.25 }}>
              Schedule Interview
            </div>
            {round && <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 400, marginTop: 2 }}>{round.round_name}</div>}
          </div>
        </div>
      }
      onCancel={onClose}
      onOk={handleSubmit}
      okText={
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <i className="ti ti-check" style={{ fontSize: 13 }} /> Schedule
        </span>
      }
      okButtonProps={{
        loading,
        style: { background: "#7c3aed", borderColor: "#7c3aed", borderRadius: 8, fontWeight: 600, height: 36 }
      }}
      cancelButtonProps={{ disabled: loading, style: { borderRadius: 8, height: 36 } }}
      width={700}
      destroyOnClose
      zIndex={1111}
      styles={{
        mask: {
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(15, 23, 42, 0.4)",
        },
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 18, marginTop: 10 }}>

        {!round && (
          <div>
            <Label text="Select Interview Round" required />
            <Select
              style={{ width: "100%" }}
              status={fieldErrors.round ? "error" : undefined}
              placeholder={jobRoundsLoading ? "Loading rounds..." : "Choose a configured round..."}
              loading={jobRoundsLoading}
              value={selectedRoundId}
              onChange={(val) => {
                setSelectedRoundId(val);
                const selected = jobRounds.find((r: any) => r.id === val);
                if (selected) {
                  setSelectedRoundType(selected.round_type);
                  setSelectedRoundNumber(selected.stage_order || 1);
                  setDuration(selected.duration_minutes || 60);
                } else {
                  setSelectedRoundType("");
                }
                setFieldErrors((e) => ({ ...e, round: "" }));
              }}
              allowClear
              options={jobRounds.map((r: any) => ({
                value: r.id,
                label: `${r.round_name} (${r.round_type})`,
              }))}
            />
            {fieldErrors.round && (
              <div style={{ color: "#ef4444", fontSize: 11, marginTop: 3 }}>{fieldErrors.round}</div>
            )}
          </div>
        )}

        <div>
          <Label text="Interview mode" required />
          <div style={{ display: "flex", gap: 6 }}>
            {INTERVIEW_MODE_OPTIONS.map(({ value, label, icon }) => {
              const active = interviewMode === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleModeChange(value)}
                  style={{
                    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                    border: `1.5px solid ${active ? "#7c3aed" : "#e2e8f0"}`,
                    background: active ? "#f3e8ff" : "#fff",
                    color: active ? "#7c3aed" : "#64748b",
                    fontSize: 12, fontWeight: active ? 700 : 500, transition: "all 0.15s",
                  }}>
                  <i className={`ti ${icon}`} style={{ fontSize: 14 }} />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <Label text="Date" required />
            <DatePicker
              style={{ width: "100%" }}
              status={fieldErrors.interviewDate ? "error" : undefined}
              onChange={(_, d) => { setInterviewDate(d as string); setFieldErrors(e => ({ ...e, interviewDate: "" })); }}
              disabledDate={(d) => d && d.isBefore(dayjs(), "day")}
            />
          </div>
          <div>
            <Label text="Time" required />
            <TimePicker
              style={{ width: "100%" }}
              status={fieldErrors.interviewTime ? "error" : undefined}
              format="HH:mm"
              onChange={(_, t) => { setInterviewTime(t as string); setFieldErrors(e => ({ ...e, interviewTime: "" })); }}
            />
          </div>
        </div>

        <div>
          <Label text={modeField.label} required={modeField.required} />
          <Input
            key={interviewMode}
            status={fieldErrors.modeField ? "error" : undefined}
            prefix={<i className={`ti ${modeField.icon}`} style={{ fontSize: 12, color: "#a78bfa" }} />}
            placeholder={modeField.placeholder}
            value={modeFieldValue}
            onChange={(e) => { handleModeFieldChange(e); setFieldErrors(prev => ({ ...prev, modeField: "" })); }}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <Label text="Duration (Minutes)" required />
            <Input type="number" value={duration} onChange={(e) => setDuration(Number(e.target.value))} />
          </div>
          <div>
            <Label text="Select Interviewers" required />
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Select interviewers..."
              value={interviewerIds}
              onChange={(vals: number[]) => setInterviewerIds(vals)}
              options={employeeOptions}
              loading={employeesLoading}
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount={3}
            />
          </div>
        </div>

        <div
          onClick={() => setSendNotification(v => !v)}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "11px 14px", borderRadius: 10, cursor: "pointer",
            border: `1px solid ${sendNotification ? "#bbf7d0" : "#e2e8f0"}`,
            background: sendNotification ? "#f0fdf4" : "#f8fafc",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, background: sendNotification ? "#dcfce7" : "#f1f5f9",
              display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <i className="ti ti-mail" style={{ fontSize: 16, color: sendNotification ? "#16a34a" : "#94a3b8" }} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>Send email to candidate</div>
            </div>
          </div>
          <Switch checked={sendNotification} onClick={(_, e) => e.stopPropagation()} />
        </div>
      </div>
    </Modal>
  );
};

export default ScheduleInterviewModal;
